/**
 * Aufnahme-Helfer für die Kamera: Beleg im Live-Bild finden, Schärfe messen,
 * eine kurze Bildserie aufnehmen und das schärfste Bild behalten.
 *
 * Warum eine Serie: Im Browser liefert das iPhone kein echtes Foto aus dem
 * Live-Bild, nur Videobilder. Bis 10/2026 wurde genau das Bild im Moment des
 * Antippens genommen — und in diesem Moment wackelt der Finger. Die Schärfe
 * der Scans schwankte dadurch um den Faktor 7. Jetzt laufen nach dem Auslösen
 * ~0,45 s Videobilder durch, jedes wird gemessen, das schärfste bleibt.
 */
import { findeBeleg, grau, schaerfe, skaliereEcken, type Ecken } from "./bild";

/** Unter diesem Wert (gemessen auf 800 px) gilt eine Aufnahme als unscharf. */
export const SCHAERFE_MIN = 0.1;
/** Kleinere Belege als dieser Bildanteil: „näher heran". */
export const FLAECHE_MIN = 0.1;

type Quelle = HTMLVideoElement | HTMLCanvasElement | ImageBitmap | HTMLImageElement;

function masse(q: Quelle): { w: number; h: number } {
  if (q instanceof HTMLVideoElement) return { w: q.videoWidth, h: q.videoHeight };
  if (q instanceof HTMLImageElement) return { w: q.naturalWidth, h: q.naturalHeight };
  return { w: q.width, h: q.height };
}

function leinwand(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

const erkennungsLeinwand = leinwand(1, 1);

/** Beleg in einem Bild suchen; Ecken in den Koordinaten des Bildes. */
export function erkenne(q: Quelle): { ecken: Ecken; flaeche: number } | null {
  const { w, h } = masse(q);
  if (!w || !h) return null;
  const s = 400 / Math.max(w, h);
  const c = erkennungsLeinwand;
  const cw = Math.max(1, Math.round(w * s));
  const ch = Math.max(1, Math.round(h * s));
  if (c.width !== cw || c.height !== ch) {
    c.width = cw;
    c.height = ch;
  }
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(q, 0, 0, cw, ch);
  const d = ctx.getImageData(0, 0, cw, ch);
  const f = findeBeleg({ data: d.data, width: cw, height: ch });
  if (!f) return null;
  return { ecken: skaliereEcken(f.ecken, w / cw), flaeche: f.flaeche };
}

/** Achsparalleler Kasten um die Ecken (oder die Bildmitte), im Bild begrenzt. */
export function kasten(ecken: Ecken | null, w: number, h: number) {
  if (!ecken) return { x: w * 0.15, y: h * 0.15, w: w * 0.7, h: h * 0.7 };
  const xs = [ecken.tl.x, ecken.tr.x, ecken.br.x, ecken.bl.x];
  const ys = [ecken.tl.y, ecken.tr.y, ecken.br.y, ecken.bl.y];
  const x0 = Math.max(0, Math.min(...xs)), x1 = Math.min(w, Math.max(...xs));
  const y0 = Math.max(0, Math.min(...ys)), y1 = Math.min(h, Math.max(...ys));
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

const messLeinwand = leinwand(1, 1);

/** Schärfe im Belegbereich, auf 800 px lange Kante gerechnet. */
export function schaerfeIn(q: Quelle, ecken: Ecken | null): number {
  const { w, h } = masse(q);
  if (!w || !h) return 0;
  const k = kasten(ecken, w, h);
  const s = Math.min(1, 800 / Math.max(k.w, k.h));
  const mw = Math.max(8, Math.round(k.w * s));
  const mh = Math.max(8, Math.round(k.h * s));
  const c = messLeinwand;
  if (c.width !== mw || c.height !== mh) {
    c.width = mw;
    c.height = mh;
  }
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return 0;
  ctx.drawImage(q, k.x, k.y, k.w, k.h, 0, 0, mw, mh);
  const d = ctx.getImageData(0, 0, mw, mh);
  return schaerfe(grau({ data: d.data, width: mw, height: mh }), mw, mh);
}

/** Wartet auf das nächste neue Videobild (oder den nächsten Frame als Rückfall). */
function naechstesBild(v: HTMLVideoElement): Promise<void> {
  return new Promise((res) => {
    const rvfc = (v as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    }).requestVideoFrameCallback;
    if (typeof rvfc === "function") rvfc.call(v, () => res());
    else requestAnimationFrame(() => res());
  });
}

/**
 * Bildserie aus dem Live-Video: jedes Bild im Belegbereich messen, das
 * schärfste in voller Auflösung behalten. Das erste Bild wird sofort
 * genommen — so ist es auch bei einer zu kurzen Serie nie leer.
 */
export async function serie(
  v: HTMLVideoElement,
  ecken: Ecken | null,
  dauerMs = 450,
): Promise<{ bild: HTMLCanvasElement; schaerfe: number; bilder: number }> {
  const { w, h } = masse(v);
  const best = leinwand(w, h);
  const bctx = best.getContext("2d")!;
  let bestS = -1;
  let bilder = 0;
  const t0 = performance.now();
  for (;;) {
    const s = schaerfeIn(v, ecken);
    bilder++;
    if (s > bestS) {
      bestS = s;
      bctx.drawImage(v, 0, 0, w, h);
    }
    if (performance.now() - t0 >= dauerMs && bilder >= 3) break;
    if (bilder >= 30) break;
    await naechstesBild(v);
  }
  return { bild: best, schaerfe: bestS, bilder };
}

export function alsJpeg(c: HTMLCanvasElement, q = 0.95): Promise<Blob> {
  return new Promise((res, rej) =>
    c.toBlob((b) => (b ? res(b) : rej(new Error("Bild konnte nicht gespeichert werden."))), "image/jpeg", q),
  );
}

/** Kleine Vorschau des Belegbereichs (für den Stapel und die Fang-Animation). */
export async function vorschau(q: Quelle, ecken: Ecken | null, max = 640): Promise<string> {
  const { w, h } = masse(q);
  const k = kasten(ecken, w, h);
  const s = Math.min(1, max / Math.max(k.w, k.h));
  const c = leinwand(k.w * s, k.h * s);
  c.getContext("2d")!.drawImage(q, k.x, k.y, k.w, k.h, 0, 0, c.width, c.height);
  const b = await alsJpeg(c, 0.8);
  return URL.createObjectURL(b);
}

/** Rahmen, wenn nichts erkannt wurde: das Bild mit etwas Rand. */
export function standardEcken(w: number, h: number): Ecken {
  const mx = w * 0.04, my = h * 0.04;
  return {
    tl: { x: mx, y: my },
    tr: { x: w - mx, y: my },
    br: { x: w - mx, y: h - my },
    bl: { x: mx, y: h - my },
  };
}

/** Größte Eckenverschiebung zwischen zwei Rahmen, relativ zur Bilddiagonale. */
export function abweichung(a: Ecken, b: Ecken, w: number, h: number): number {
  const d = Math.hypot(w, h) || 1;
  const k: (keyof Ecken)[] = ["tl", "tr", "br", "bl"];
  return Math.max(...k.map((n) => Math.hypot(a[n].x - b[n].x, a[n].y - b[n].y))) / d;
}
